
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-cream px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <Link to="/auth" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>

        <h1 className="font-display text-3xl mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: February 15, 2026</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold mb-2">1. Introduction</h2>
            <p>Birdie Game ("the App," "we," "us," or "our") respects your privacy. This Privacy Policy explains what information we collect, how we use it, how we share it, and what choices you have. This policy applies to all users of the Birdie Game application.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">2. Information We Collect</h2>

            <h3 className="font-medium mt-3 mb-1">2.1 Information from Google Sign-In</h3>
            <p className="mb-2">When you sign in using Google OAuth, we receive and store:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Your Google profile photo (avatar)</li>
              <li>Your Google account identifier</li>
            </ul>
            <p className="mt-2">We do not receive or store your Google password.</p>

            <h3 className="font-medium mt-3 mb-1">2.2 Information You Provide</h3>
            <p className="mb-2">Through your use of the App, we collect:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Golf scores, including birdies, eagles, and double eagles</li>
              <li>Golf course selections and home course preference</li>
              <li>Friend connections (which users you connect with)</li>
              <li>Game participation and game settings</li>
            </ul>

            <h3 className="font-medium mt-3 mb-1">2.3 Information Collected Automatically</h3>
            <p className="mb-2">When you use the App, we may automatically collect:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Basic usage data (pages visited, features used)</li>
              <li>Device type and browser information</li>
              <li>Error and crash reports for troubleshooting</li>
            </ul>
            <p className="mt-2">We do not use third-party analytics or advertising trackers.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">3. How We Use Your Information</h2>
            <p className="mb-2">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Provide, operate, and maintain the App</li>
              <li>Authenticate your identity and manage your account</li>
              <li>Display your profile to friends and game participants</li>
              <li>Record and display your golf scores and birdie history</li>
              <li>Enable social features such as friend connections and shared games</li>
              <li>Improve the App and fix bugs</li>
              <li>Communicate with you about service-related matters</li>
            </ul>
            <p className="mt-2">We do not use your information for advertising. We do not sell your information.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">4. How We Share Your Information</h2>

            <h3 className="font-medium mt-3 mb-1">4.1 With Other Users</h3>
            <p className="mb-2">When you use the App's social features:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your name and avatar are visible to other users when they search for friends</li>
              <li>Friends you connect with can see your birdie scores, game participation, and course activity</li>
              <li>Other participants in a shared game can see your scores within that game</li>
            </ul>

            <h3 className="font-medium mt-3 mb-1">4.2 With Service Providers</h3>
            <p className="mb-2">We use the following third-party services to operate the App:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Supabase</strong> — our backend infrastructure provider, which stores your account data, scores, and game information in cloud-hosted databases</li>
              <li><strong>Google</strong> — provides authentication services through Google OAuth</li>
            </ul>

            <h3 className="font-medium mt-3 mb-1">4.3 As Required by Law</h3>
            <p>We may disclose your information if required to do so by law or in response to valid legal requests by public authorities.</p>

            <h3 className="font-medium mt-3 mb-1">4.4 No Sale of Data</h3>
            <p>We do not sell, rent, or trade your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">5. Data Storage and Security</h2>
            <p className="mb-2">Your data is stored on Supabase's cloud infrastructure. We implement reasonable security measures including:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Authentication via secure OAuth protocols</li>
              <li>Row-level security policies on database tables</li>
            </ul>
            <p className="mt-2">No method of electronic storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">6. Data Retention</h2>
            <p>We retain your data for as long as your account is active. If you request account deletion, we will delete your personal data within 30 days, except where we are required to retain it by law. Aggregated or anonymized data that cannot identify you may be retained indefinitely.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">7. Your Rights and Choices</h2>
            <p className="mb-2">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Access</strong> the personal data we hold about you</li>
              <li><strong>Correct</strong> inaccurate or incomplete data</li>
              <li><strong>Delete</strong> your account and associated data</li>
              <li><strong>Export</strong> your data in a portable format</li>
              <li><strong>Withdraw consent</strong> for data processing by deleting your account</li>
              <li><strong>Remove friend connections</strong> at any time to stop sharing scores with specific users</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <a href="mailto:support@birdiechase.com" className="text-primary underline">support@birdiechase.com</a>. We will respond to requests within 30 days.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">8. Children's Privacy</h2>
            <p>The App is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13, we will take steps to delete that information promptly. If you believe a child under 13 has provided us with personal information, please contact us at <a href="mailto:support@birdiechase.com" className="text-primary underline">support@birdiechase.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">9. California Residents (CCPA)</h2>
            <p className="mb-2">If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>The right to know what personal information we collect and how it is used</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt out of the sale of personal information (we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">10. International Users</h2>
            <p>If you access the App from outside the United States, your data may be transferred to and processed in the United States where our service providers operate. By using the App, you consent to this transfer.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify you by updating the effective date at the top of this policy. Your continued use of the App after changes are posted constitutes your acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy or your data, contact us at <a href="mailto:support@birdiechase.com" className="text-primary underline">support@birdiechase.com</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
